import type { TFunction } from 'i18next';

/**
 * Shared types for the member-facing course (kroužek) presentation surfaces.
 * These mirror the public/my activity payloads returned by the backend.
 */
export type RecurringPattern = 'daily' | 'weekly' | 'biweekly' | 'monthly';

export interface ISchedule {
  templateId: number;
  name: string;
  location: string | null;
  /** 0 = Sunday .. 6 = Saturday */
  dayOfWeek: number;
  /** 'HH:MM' */
  startTime: string;
  /** 'HH:MM' */
  endTime: string;
  recurringPattern: RecurringPattern;
  isActive: boolean;
}

export interface ICourseColorSettings {
  primaryColor?: string;
  secondaryColor?: string;
}

export interface ICourse {
  _id: number;
  name: string;
  description: string | null;
  capacity: number | null;
  enrollmentPolicy: 'open' | 'approval';
  coverImage: string | null;
  gallery: string[] | null;
  extendedDescription: string | null;
  colorSettings: ICourseColorSettings | null;
  schedule: ISchedule[];
  /** Optional drop-in price for a single session (decimal; may arrive as a string). */
  pricePerSession?: string | number | null;
  /** Populated payment plans offered for this course. */
  memberships?: {
    _id: number;
    name: string;
    type: string;
    entryCount?: number | null;
    /** Lowest derived price across this membership's options (backward-compat). */
    price?: string | number | null;
    /** Derived priced options (calendar intervals, time tiers, entry packages). */
    priceOptions?: {
      amount: number;
      interval?: string;
      durationDays?: number;
      entryCount?: number;
    }[];
    /**
     * Season-division definition (`type === 'season'` only). Carries fullPrice
     * plus each split lane's per-segment prices, which is what lets the pricing
     * modal show full / half / quarter tiers instead of one collapsed row.
     * Typed as unknown so this module stays free of the schemaForm import;
     * callers narrow via `coerceDivisionScheme`.
     */
    divisionScheme?: unknown;
  }[];
  /** Present only on the /api/my/activities payload. */
  enrolledMemberIds?: number[];
  /** Present only on the /api/my/activities payload. */
  pendingMemberIds?: number[];
  /** Current number of enrolled members (member-facing public/my payloads). */
  enrolledCount?: number;
  /** Course term (season) — ISO date strings or null. */
  termStart?: string | null;
  termEnd?: string | null;
  /**
   * The active season this course's term/capacity/pricing came from. Needed when
   * buying a membership offered across several seasons, which requires an
   * explicit seasonId.
   */
  activeSeasonId?: number | null;
  /** Derived: the course's term has ended (termEnd < today). */
  isExpired?: boolean;
}

/**
 * An activity (browse unit) bundled with its courses (kroužky), as returned
 * by the public/my activity payloads. Shared by the activity poster card and
 * the activity detail modal.
 */
export interface IActivityWithCourses {
  staff?: import('../components/StaffProfiles').StaffPerson[];
  _id: number;
  name: string;
  description?: string | null;
  coverImage?: string | null;
  joinPolicy?: string;
  pendingMemberIds?: number[];
  courses: ICourse[];
}

/**
 * Localize a weekday name from a 0..6 (Sun..Sat) index without needing
 * dedicated day-name i18n keys. We construct a Date that lands on the
 * requested weekday and let the runtime format it for the locale.
 */
export const weekdayName = (dayOfWeek: number, locale: string): string => {
  // 2024-01-07 is a Sunday; add the offset to reach the requested weekday.
  const base = new Date(2024, 0, 7 + ((dayOfWeek % 7) + 7) % 7);
  return base.toLocaleDateString(locale || undefined, { weekday: 'short' });
};

/**
 * Localized recurrence label via `courses.recurrence.<pattern>` keys.
 */
export const recurrenceLabel = (pattern: RecurringPattern, t: TFunction): string =>
  t(`courses.recurrence.${pattern}`);

/**
 * Sort schedule entries by weekday then start time for stable rendering.
 */
export const sortSchedule = (schedule: ISchedule[]): ISchedule[] =>
  [...schedule].sort((a, b) => {
    if (a.dayOfWeek !== b.dayOfWeek) return a.dayOfWeek - b.dayOfWeek;
    return a.startTime.localeCompare(b.startTime);
  });

/**
 * Presentational summary of a course's weekly schedule, used by the poster card
 * to show compact day chips plus a time line.
 */
export interface IScheduleSummary {
  /** De-duplicated short weekday names in weekday/time order, e.g. ['Mon','Wed','Fri']. */
  dayLabels: string[];
  /** A single shared time range (e.g. '18:00–19:00') when all entries match, else null. */
  timeLabel: string | null;
  /**
   * The common location when every scheduled entry shares the same non-null
   * location, else null. Pairs with the shared `timeLabel` case.
   */
  location: string | null;
  /** Per-entry day + time + location, used when times differ across days. */
  perEntry: { day: string; time: string; location: string | null }[];
}

/**
 * Summarize a weekly schedule into day chips + a time line. Pure/presentational.
 * Sorts by weekday then start time. When every entry shares the same start/end
 * time, `timeLabel` carries that single range and the card shows day chips on one
 * line with the time beneath. When times differ, `timeLabel` is null and the card
 * renders the `perEntry` lines instead (e.g. "Mon 18:00–19:00", "Fri 17:00–18:00").
 */
export const summarizeSchedule = (
  schedule: ISchedule[],
  locale: string
): IScheduleSummary => {
  const sorted = sortSchedule(schedule || []);

  const dayLabels: string[] = [];
  const perEntry: { day: string; time: string; location: string | null }[] = [];
  for (const entry of sorted) {
    const day = weekdayName(entry.dayOfWeek, locale);
    if (!dayLabels.includes(day)) dayLabels.push(day);
    perEntry.push({
      day,
      time: `${entry.startTime}–${entry.endTime}`,
      location: entry.location,
    });
  }

  const allSameTime =
    sorted.length > 0 &&
    sorted.every(
      (e) => e.startTime === sorted[0].startTime && e.endTime === sorted[0].endTime
    );
  const timeLabel = allSameTime
    ? `${sorted[0].startTime}–${sorted[0].endTime}`
    : null;

  // Common location when every entry shares the same non-null location.
  const allSameLocation =
    sorted.length > 0 &&
    sorted[0].location != null &&
    sorted.every((e) => e.location === sorted[0].location);
  const location = allSameLocation ? sorted[0].location : null;

  return { dayLabels, timeLabel, location, perEntry };
};

/**
 * Render a single schedule line, e.g. "Mon 18:00–19:00 (weekly)".
 * startTime/endTime are already 'HH:MM' and kept as-is.
 */
export const formatScheduleEntry = (
  entry: ISchedule,
  locale: string,
  t: TFunction
): string => {
  const day = weekdayName(entry.dayOfWeek, locale);
  const time = `${entry.startTime}–${entry.endTime}`;
  const recurrence = recurrenceLabel(entry.recurringPattern, t);
  return `${day} ${time} (${recurrence})`;
};
